/**
 * Image Edit Module
 * Handles editing existing images using Nano Banana Pro Edit API
 *
 * Features:
 * - Edit modal with image preview
 * - Resolution and aspect ratio options
 * - Queue-based editing with polling
 * - Image replacement in cards
 * - Contextual editing for scene frames (v63)
 * - R2 upload for permanent storage (v67)
 * v67 - Added R2 upload to persist fal.media URLs
 */

// v67 - Self-rendering module with context support and R2 upload
import { GeneratorState, API_CONFIG, persistSessionState } from '../core/state.js';
import { getIdeaImageUrl, STYLE_GUIDES, getEffectiveStyle, saveImageToDatabase } from './image-generation.js';
import { injectModalHtml } from '../../../js/shared/dom-utils.js';
import { renderHistoryButton } from './version-history.js';
import { onDocument, cleanupScope } from '../../../js/core/events.js';
import { showError } from '../../../js/core/error-handler.js';
import { getContextBarHTML, updateContextBar, setupContextToggle } from '../../../js/shared/modal-context-bar.js';
// CREDIT-FIX: Import credit animation for visual feedback
import { animateCoinSpend, refreshCreditsFromServer } from '../../../js/shared/credit-animation.js';
// Centralized pricing - Single Source of Truth (see frontend/js/core/pricing.js)
import { getImageEditCost, formatCredits } from '../../../js/core/pricing.js';

// Image edit cost from centralized pricing
const IMAGE_EDIT_COST = getImageEditCost();

// ============================================================================
// HTML TEMPLATE - Self-rendering modal (window-titlebar version)
// ============================================================================

const MODAL_HTML = `
<div class="modal-overlay" id="image-edit-modal-overlay">
  <div class="retro-modal image-edit-modal">
    <div class="window-titlebar modal-titlebar">
      <div class="window-close modal-close-btn" id="edit-modal-close" aria-label="Close"></div>
      <div class="window-title">◆ EDIT IMAGE</div>
    </div>
    <div class="modal-idea-subtitle" id="edit-modal-idea-title"></div>
    <div class="modal-body edit-modal-body">
      <div class="edit-image-preview-section">
        <label class="edit-modal-label">CURRENT IMAGE</label>
        <div class="edit-image-preview-container">
          <img id="edit-modal-preview-img" src="" alt="Current image" />
        </div>
      </div>
      <div class="edit-prompt-section">
        <label class="edit-modal-label" for="edit-prompt-textarea">DESCRIBE YOUR EDIT</label>
        <textarea id="edit-prompt-textarea" class="edit-modal-textarea" rows="5"
          placeholder="Describe the changes you want to make...

Examples:
- Change the background to a sunset beach
- Add a person holding the product
- Make the colors more vibrant
- Remove the text and add new headline"></textarea>
      </div>
      <div class="edit-options-section">
        <div class="edit-option-group">
          <label class="edit-modal-label">RESOLUTION</label>
          <div class="edit-resolution-buttons">
            <button class="resolution-btn active" data-resolution="1K">1K</button>
            <button class="resolution-btn" data-resolution="2K">2K</button>
            <button class="resolution-btn" data-resolution="4K">4K</button>
          </div>
        </div>
        <div class="edit-option-group">
          <label class="edit-modal-label">ASPECT RATIO</label>
          <div class="edit-aspect-buttons">
            <button class="edit-aspect-btn active" data-aspect="auto">AUTO</button>
            <button class="edit-aspect-btn" data-aspect="16:9">16:9</button>
            <button class="edit-aspect-btn" data-aspect="4:3">4:3</button>
            <button class="edit-aspect-btn" data-aspect="1:1">1:1</button>
            <button class="edit-aspect-btn" data-aspect="3:4">3:4</button>
            <button class="edit-aspect-btn" data-aspect="9:16">9:16</button>
          </div>
        </div>
        <div class="edit-option-group">
          <label class="edit-modal-label">STYLE</label>
          <select class="image-gen-select" id="edit-style-selector">
            <option value="auto" selected>Auto (No Style)</option>
            <option value="advertising">Advertising</option>
            <option value="editorial">Editorial</option>
            <option value="minimal">Minimal</option>
            <option value="bold">Bold</option>
            <option value="8bit-bw">8-Bit B&W</option>
            <option value="random">Random</option>
          </select>
        </div>
      </div>
      ${getContextBarHTML('edit-gen')}
      <div class="edit-cost-section">
        <span class="edit-cost-label">ESTIMATED COST:</span>
        <span class="edit-cost-value" id="edit-cost-value">~$0.20 (20 C)</span>
      </div>
      <div class="edit-info-tip">
        <span class="info-icon">i</span>
        <span class="info-text">The AI will modify your image based on your description while preserving the overall composition.</span>
      </div>
    </div>
    <div class="modal-footer edit-modal-footer">
      <button class="btn btn-ghost" id="edit-modal-cancel">CANCEL</button>
      <button class="btn btn-primary" id="edit-modal-submit">
        <span class="btn-text">APPLY EDIT <span class="btn-credits" id="edit-btn-credits"><span class="pixel-coin-static xs"></span> <span class="btn-credits-value">${IMAGE_EDIT_COST}</span></span></span>
        <span class="btn-loading" style="display: none;">
          <span class="pixel-spinner"></span>
          EDITING...
        </span>
      </button>
    </div>
  </div>
</div>
`;

// ============================================================================
// MODULE STATE
// ============================================================================

let currentEditIdeaId = null;
let currentEditImageUrl = null;
// Task 102: Converted from single variable to object for concurrent edit support
const editPollingIntervals = {};  // Keyed by composite editKey
const editGenerations = {};       // Track generation state per key
// Context tracking for scene frame editing
// null = main idea image, { type: 'scene', sceneSeq: 1, frame: 'start' } = scene frame
let editContext = null;

// ============================================================================
// COMPOSITE KEY GENERATION (Task 102: Fix concurrent edit collisions)
// ============================================================================

/**
 * Generate unique key for edit tracking
 * @param {string} ideaId - The idea ID
 * @param {Object|null} context - Edit context (scene frame or panel info)
 * @returns {string}
 */
function getEditKey(ideaId, context = null) {
  if (context?.type === 'scene' && context?.sceneSeq != null) {
    return `${ideaId}_edit_${context.sceneSeq}_${context.frame}`;
  }
  if (context?.type === 'panel' && context?.panelIndex != null) {
    return `${ideaId}_edit_panel_${context.panelIndex}`;
  }
  return `${ideaId}_edit`;
}

/**
 * Extract ideaId from a composite edit key
 * @param {string} editKey - The composite edit key
 * @returns {string} - The original idea ID
 */
function extractIdeaIdFromEditKey(editKey) {
  // Handle various formats: "uuid_edit", "uuid_edit_N_type", "uuid_edit_panel_N"
  const parts = editKey.split('_edit');
  return parts[0];
}

/**
 * Check if an edit is in progress for a specific context
 * Task 102: Added for concurrent edit support
 * @param {string} ideaId
 * @param {Object|null} context
 * @returns {boolean}
 */
export function isEditInProgress(ideaId, context = null) {
  const editKey = getEditKey(ideaId, context);
  return editGenerations[editKey]?.status === 'IN_PROGRESS';
}

/**
 * Check if ANY edit is in progress for an idea (any context)
 * @param {string} ideaId - The idea ID
 * @returns {boolean}
 */
export function isAnyEditInProgressForIdea(ideaId) {
  const id = String(ideaId);
  for (const [editKey, state] of Object.entries(editGenerations)) {
    if (editKey.startsWith(`${id}_edit`)) {
      if (state.status === 'IN_PROGRESS') {
        return true;
      }
    }
  }
  return false;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const EDIT_MODEL_ID = 'fal-ai/nano-banana-pro/edit';
const POLL_INTERVAL = 2000; // 2 seconds
const MAX_POLL_TIME = 180000; // 3 minutes

// API endpoints
const EDIT_ENDPOINTS = {
  submit: '/api/image-gen/edit/submit',
  status: (modelId, requestId) => `/api/image-gen/status/${encodeURIComponent(modelId)}/${requestId}`,
  result: (modelId, requestId) => `/api/image-gen/result/${encodeURIComponent(modelId)}/${requestId}`
};

function getApiUrl(endpoint) {
  return API_CONFIG.baseUrl + endpoint;
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize the image edit module
 * Called from phase4-harvest.js
 */
export function initImageEdit() {
  // Self-render: inject modal HTML if not already present
  injectModalHtml('image-edit-modal-overlay', MODAL_HTML);

  // Get modal elements
  const overlay = document.getElementById('image-edit-modal-overlay');
  const closeBtn = document.getElementById('edit-modal-close');
  const cancelBtn = document.getElementById('edit-modal-cancel');
  const submitBtn = document.getElementById('edit-modal-submit');

  if (!overlay) {
    console.warn('[ImageEdit] Modal elements not found');
    return;
  }

  // Close handlers
  closeBtn?.addEventListener('click', closeEditModal);
  cancelBtn?.addEventListener('click', closeEditModal);

  // Close on overlay click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      closeEditModal();
    }
  });

  // Close on Escape - use scoped listener for proper cleanup
  onDocument('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('active')) {
      closeEditModal();
    }
  }, 'image-edit');

  // Submit handler
  submitBtn?.addEventListener('click', submitEdit);

  // Resolution button handlers
  const resolutionBtns = overlay.querySelectorAll('.resolution-btn');
  resolutionBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      resolutionBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
    });
  });

  // Aspect ratio button handlers
  const aspectBtns = overlay.querySelectorAll('.edit-aspect-btn');
  aspectBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      aspectBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
    });
  });

  // Event delegation for edit buttons on cards
  initEditButtonEvents();

  console.log('[ImageEdit] Module initialized');
}

/**
 * Initialize edit button event delegation
 * Uses document-level delegation for dynamically created buttons
 */
function initEditButtonEvents() {
  // Prevent duplicate event binding
  if (window._imgEditEventsInitialized) {
    console.log('[ImageEdit] Event handlers already initialized');
    return;
  }

  // Event delegation on document.body for edit buttons
  document.body.addEventListener('click', (e) => {
    const editBtn = e.target.closest('.btn-edit');
    if (editBtn) {
      e.preventDefault();
      e.stopPropagation();
      const card = editBtn.closest('.idea-card');
      if (card) {
        const ideaId = card.dataset.id;
        openEditModal(ideaId);
      }
    }
  });

  // Mark as initialized
  window._imgEditEventsInitialized = true;
}

// ============================================================================
// MODAL HANDLING
// ============================================================================

/**
 * Open the edit modal for a specific idea OR scene frame
 * @param {string} ideaId - The idea ID
 * @param {Object|null} context - Optional context for scene frame editing
 *   { type: 'scene', sceneSeq: 1, frame: 'start' | 'end' }
 */
export async function openEditModal(ideaId, context = null) {
  // v68 Fix: Ensure modal HTML is injected (fixes Canvas edit bug)
  // Canvas imports openEditModal but not initImageEdit, so modal may not exist
  let overlay = document.getElementById('image-edit-modal-overlay');
  if (!overlay) {
    console.log('[ImageEdit] Modal not initialized, injecting HTML');
    initImageEdit();
  }

  // Store context for later use in save logic
  editContext = context;

  let imageUrl;
  let displayTitle = 'Untitled Idea';

  if (context && context.type === 'scene') {
    // SCENE CONTEXT: Get image from storyboard state
    // Import directly from module to ensure we get the same state as ui.js
    const { getStoryboard } = await import('../storyboard/state.js');
    const storyboard = getStoryboard(ideaId);

    if (!storyboard) {
      console.error('[ImageEdit] Storyboard not found for idea:', ideaId);
      showError('Storyboard not found. Please refresh the page.', { context: 'opening edit modal' });
      editContext = null;
      return;
    }

    const scene = storyboard.scenes?.find(s => s.seq === context.sceneSeq);
    if (!scene || !scene.frames || !scene.frames[context.frame]) {
      console.error('[ImageEdit] Frame not found:', context);
      showError('No image in this frame to edit. Generate or upload an image first.', { context: 'opening edit modal' });
      editContext = null;
      return;
    }

    imageUrl = scene.frames[context.frame].url;
    displayTitle = `Scene ${context.sceneSeq} — ${context.frame.toUpperCase()} Frame`;

  } else if (context && context.type === 'panel') {
    // PANEL CONTEXT: Get image from panel pool
    // Import directly from module to ensure we get the same state as ui.js
    const { getPanelPool } = await import('../storyboard/state.js');
    const panels = getPanelPool(ideaId);

    if (!panels || !panels[context.panelIndex]) {
      console.error('[ImageEdit] Panel not found:', context, 'Pool has', panels?.length || 0, 'panels');
      showError('Panel not found. Please refresh the page.', { context: 'opening edit modal' });
      editContext = null;
      return;
    }

    const panel = panels[context.panelIndex];
    imageUrl = panel.dataUrl || panel.url;

    if (!imageUrl) {
      console.error('[ImageEdit] Panel has no image:', context);
      showError('No image in this panel to edit.', { context: 'opening edit modal' });
      editContext = null;
      return;
    }

    displayTitle = `Panel ${context.panelIndex + 1}`;

  } else {
    // DEFAULT CONTEXT: Main idea image
    imageUrl = getIdeaImageUrl(ideaId);

    // Get idea for title
    const idea = findIdea(ideaId);
    displayTitle = idea?.title || 'Untitled Idea';
  }

  if (!imageUrl) {
    console.error('[ImageEdit] No image found for idea:', ideaId);
    showError('No image available to edit.', { context: 'opening edit modal' });
    editContext = null;
    return;
  }

  // Validate it's a real remote URL, not a dummy placeholder
  if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
    console.warn('[ImageEdit] Cannot edit local/dummy image:', imageUrl);
    showError('Cannot edit placeholder images. Generate an AI image first.', { context: 'opening edit modal' });
    editContext = null;
    return;
  }

  // Store current state
  currentEditIdeaId = ideaId;
  currentEditImageUrl = imageUrl;

  // Update modal content
  const titleEl = document.getElementById('edit-modal-idea-title');
  const previewImg = document.getElementById('edit-modal-preview-img');
  const textarea = document.getElementById('edit-prompt-textarea');

  if (titleEl) titleEl.textContent = displayTitle;
  if (previewImg) previewImg.src = imageUrl;
  if (textarea) textarea.value = '';

  // Reset options to defaults
  resetEditOptions();

  // Reset submit button state
  resetSubmitButton();

  // Update context bar with brand/product info
  // Find the idea for context data (needed for Dashboard mode)
  const idea = findIdea(ideaId);
  const contextData = {
    brand: GeneratorState.currentBrand || GeneratorState.sessionBrand || idea?.meta?.brand,
    brief: idea?.meta?.brief || GeneratorState.brief,
    selectedProduct: GeneratorState.selectedProduct || GeneratorState.sessionProduct
  };
  updateContextBar('edit-gen', contextData);
  setupContextToggle('edit-gen', false); // Start expanded

  // Show modal (re-fetch in case it was just initialized)
  overlay = document.getElementById('image-edit-modal-overlay');
  overlay?.classList.add('active');
  document.body.style.overflow = 'hidden';

  // Focus textarea
  setTimeout(() => textarea?.focus(), 100);

  console.log('[ImageEdit] Modal opened for:', ideaId, 'Context:', context);
}

/**
 * Close the edit modal
 * @param {boolean} wasSubmitted - Whether the modal was closed after submitting (not canceled)
 */
export function closeEditModal(wasSubmitted = false) {
  const overlay = document.getElementById('image-edit-modal-overlay');
  overlay?.classList.remove('active');
  document.body.style.overflow = '';

  // Cleanup scoped event listeners
  cleanupScope('image-edit');

  // If modal was canceled (not submitted), dispatch event to hide overlays
  if (!wasSubmitted && editContext) {
    const savedContext = { ...editContext };
    const savedIdeaId = currentEditIdeaId;

    // Dispatch cancel event so UI can hide loading overlays
    window.dispatchEvent(new CustomEvent('storyboard:editCanceled', {
      detail: {
        ideaId: savedIdeaId,
        context: savedContext
      }
    }));

    console.log('[ImageEdit] Edit canceled, dispatching cancel event');
  }

  // Clear state including context
  currentEditIdeaId = null;
  currentEditImageUrl = null;
  editContext = null;

  // Task 102: Note - we don't clear polling intervals here on modal close
  // because the edit is still in progress. Polling cleanup happens on completion/error.
  // We only clear modal-related state.

  console.log('[ImageEdit] Modal closed');
}

// ============================================================================
// EDIT SUBMISSION
// ============================================================================

/**
 * Submit the edit request
 */
async function submitEdit() {
  const textarea = document.getElementById('edit-prompt-textarea');
  const userPrompt = textarea?.value?.trim();

  if (!userPrompt) {
    showError('Please describe the edit you want to make.', { context: 'submitting edit' });
    textarea?.focus();
    return;
  }

  if (!currentEditIdeaId || !currentEditImageUrl) {
    console.error('[ImageEdit] Missing idea or image URL');
    return;
  }

  // Get selected options
  const resolution = getSelectedResolution();
  const aspectRatio = getSelectedAspectRatio();
  const style = getSelectedStyle();

  // Build final prompt with style guide (if applicable)
  const prompt = buildEditPromptWithStyle(userPrompt, style);

  // Show loading state
  setSubmitLoading(true);

  // Store ideaId and context locally before closing modal
  const ideaId = currentEditIdeaId;
  const originalImageUrl = currentEditImageUrl;
  const savedContext = editContext ? { ...editContext } : null;

  // Only show editing placeholder on MAIN IDEA card (not for scene frames or panels)
  // Panels/frames have their own overlay shown by ui.js
  if (!savedContext || savedContext.type === 'idea') {
    showEditingPlaceholder(ideaId);
  }

  try {
    console.log('[ImageEdit] Submitting edit:', {
      ideaId,
      userPrompt,
      style,
      resolution,
      aspectRatio
    });

    // Submit to queue with auth
    const { getSessionToken } = await import('../../auth/js/client.js');
    const token = await getSessionToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(getApiUrl(EDIT_ENDPOINTS.submit), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        imageUrl: originalImageUrl,
        prompt,
        options: {
          resolution,
          aspectRatio
        }
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to submit edit');
    }

    // CREDIT-FIX: Animate credit deduction using centralized pricing
    animateCoinSpend(IMAGE_EDIT_COST);

    console.log('[ImageEdit] Edit queued:', result);

    // Task 102: Generate composite key for concurrent edit tracking
    const editKey = getEditKey(ideaId, savedContext);

    // Task 102: Store edit state with composite key
    editGenerations[editKey] = {
      status: 'IN_PROGRESS',
      ideaId: ideaId,
      context: savedContext,
      requestId: result.requestId,
      modelId: result.modelId,
      originalImageUrl: originalImageUrl
    };

    // Dispatch event to show editing overlay on panel/frame (ui.js listens)
    if (savedContext && (savedContext.type === 'scene' || savedContext.type === 'panel')) {
      window.dispatchEvent(new CustomEvent('storyboard:editSubmitted', {
        detail: { ideaId, context: savedContext }
      }));
    }

    // Close modal (pass true to indicate this was a submit, not a cancel)
    // Note: savedContext was already captured at start of function
    closeEditModal(true);

    // Task 102: Start polling with composite key
    startEditPolling(editKey, result.modelId, result.requestId, originalImageUrl);

  } catch (error) {
    console.error('[ImageEdit] Submit error:', error);
    showError(error, { context: 'submitting edit' });
    setSubmitLoading(false);
    // Remove editing placeholder, restore original
    updateIdeaImage(ideaId, originalImageUrl);
    // CREDIT-FIX: Rollback optimistic animation by refreshing true balance
    refreshCreditsFromServer();
  }
}

// ============================================================================
// POLLING
// ============================================================================

/**
 * Upload a temporary URL (fal.media) to R2 for permanent storage
 * @param {string} sourceUrl - The temporary image URL
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
async function uploadToR2(sourceUrl) {
  // Skip if already an R2 URL or local URL
  if (sourceUrl.includes('r2.dev') || sourceUrl.startsWith('data:')) {
    console.log('[ImageEdit] URL already permanent, skipping R2 upload');
    return { success: true, url: sourceUrl };
  }

  try {
    console.log('[ImageEdit] Uploading to R2 for permanent storage...');

    // Get auth token for proper user folder storage
    const { getSessionToken } = await import('../../auth/js/client.js');
    const token = await getSessionToken();

    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_CONFIG.baseUrl}/api/storage/upload-url`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ sourceUrl })
    });

    const result = await response.json();

    if (result.success) {
      console.log('[ImageEdit] R2 upload success:', result.url);
      return { success: true, url: result.url };
    } else {
      console.warn('[ImageEdit] R2 upload failed:', result.error);
      // Return original URL as fallback (will expire, but better than nothing)
      return { success: false, url: sourceUrl, error: result.error };
    }
  } catch (error) {
    console.error('[ImageEdit] R2 upload error:', error);
    // Return original URL as fallback
    return { success: false, url: sourceUrl, error: error.message };
  }
}

/**
 * Handle successful edit completion
 * Routes the updated image to the correct target (main idea, scene frame, or panel)
 * @param {string} ideaId - The idea ID
 * @param {string} imageUrl - The new image URL (may be temporary fal.media URL)
 * @param {Object|null} context - The edit context
 *   null = main idea
 *   { type: 'scene', sceneSeq, frame } = scene frame
 *   { type: 'panel', panelIndex } = panel in library
 */
async function handleEditSuccess(ideaId, imageUrl, context) {
  console.log('[ImageEdit] Edit success. Context:', context);

  // CREDIT-FIX: Refresh credits from server to show true balance
  refreshCreditsFromServer();

  // Show image IMMEDIATELY with FAL.ai URL (don't wait for R2!)
  // Backend queues R2 transfer via saveToDatabase -> queueTransfer()
  // FAL.ai URLs expire in 48h which is plenty of time for user session

  if (context && context.type === 'scene') {
    // SCENE FRAME: Update storyboard state
    try {
      const { setSceneFrame, saveToDatabase, addPanelToPool } = await import('../storyboard/state.js');

      // Update the scene frame in StoryboardState IMMEDIATELY
      setSceneFrame(context.sceneSeq, context.frame, imageUrl, 'edited');

      // Add edited image to panel pool for reuse in other scenes
      addPanelToPool(ideaId, {
        url: imageUrl,
        dataUrl: imageUrl,
        source: 'edited',
        sceneSeq: context.sceneSeq,
        frameType: context.frame,
        createdAt: new Date().toISOString()
      });

      // Dispatch event to update UI IMMEDIATELY (storyboard listens for this)
      window.dispatchEvent(new CustomEvent('storyboard:frameUpdated', {
        detail: {
          ideaId,
          sceneSeq: context.sceneSeq,
          frameType: context.frame,
          imageUrl: imageUrl
        }
      }));

      console.log(`[ImageEdit] Updated Scene ${context.sceneSeq} ${context.frame} frame`);

      // Persist to database (non-blocking for UI - backend queues R2 transfer)
      const saved = await saveToDatabase(ideaId);
      if (saved) {
        console.log(`[ImageEdit] Scene frame ${context.sceneSeq}/${context.frame} saved to database`);
      } else {
        console.warn(`[ImageEdit] Scene frame ${context.sceneSeq}/${context.frame} saved locally (not a DB idea)`);
      }
    } catch (error) {
      console.error('[ImageEdit] Failed to update scene frame:', error);
      // Fall back to main idea update
      updateIdeaImage(ideaId, imageUrl);
    }

  } else if (context && context.type === 'panel') {
    // PANEL: Update panel in pool
    try {
      const { updatePanelInPool, setPanelEditingStatus, saveToDatabase } = await import('../storyboard/state.js');

      // Clear editing status
      setPanelEditingStatus(ideaId, context.panelIndex, false);

      // Update the panel with new image IMMEDIATELY
      updatePanelInPool(ideaId, context.panelIndex, imageUrl, 'edited');

      // Dispatch event to update UI IMMEDIATELY
      window.dispatchEvent(new CustomEvent('storyboard:panelUpdated', {
        detail: {
          ideaId,
          panelIndex: context.panelIndex,
          imageUrl: imageUrl
        }
      }));

      console.log(`[ImageEdit] Updated Panel ${context.panelIndex + 1}`);

      // Persist to database (non-blocking for UI - backend queues R2 transfer)
      saveToDatabase(ideaId);
    } catch (error) {
      console.error('[ImageEdit] Failed to update panel:', error);
      // No fallback for panels - just log the error
    }

  } else {
    // MAIN IDEA: Update idea image IMMEDIATELY (backend queues R2 transfer)
    updateIdeaImage(ideaId, imageUrl);
  }
}

/**
 * Start polling for edit completion
 * Task 102: Refactored to use composite keys for concurrent edit tracking
 * @param {string} editKey - Composite edit key
 * @param {string} modelId - The model ID from submission
 * @param {string} requestId - The request ID from submission
 * @param {string} originalImageUrl - Original image URL for rollback
 */
async function startEditPolling(editKey, modelId, requestId, originalImageUrl) {
  // Clear any existing polling for this key
  if (editPollingIntervals[editKey]) {
    clearInterval(editPollingIntervals[editKey]);
  }

  // Task 102: Extract ideaId and context from stored state
  const state = editGenerations[editKey];
  const ideaId = state?.ideaId || extractIdeaIdFromEditKey(editKey);
  const context = state?.context;

  const startTime = Date.now();

  // Get auth token for authenticated polling requests
  const { getSessionToken } = await import('../../auth/js/client.js');
  const token = await getSessionToken();
  const authHeaders = { 'Content-Type': 'application/json' };
  if (token) {
    authHeaders['Authorization'] = `Bearer ${token}`;
  }

  console.log('[ImageEdit] Starting poll for:', { editKey, ideaId, modelId, requestId, context });

  editPollingIntervals[editKey] = setInterval(async () => {
    // Check timeout
    if (Date.now() - startTime > MAX_POLL_TIME) {
      clearInterval(editPollingIntervals[editKey]);
      delete editPollingIntervals[editKey];
      delete editGenerations[editKey];
      showEditError(ideaId, 'Edit timed out. Please try again.', originalImageUrl);
      return;
    }

    try {
      const statusResponse = await fetch(getApiUrl(EDIT_ENDPOINTS.status(modelId, requestId)), {
        headers: authHeaders
      });
      const statusResult = await statusResponse.json();

      console.log('[ImageEdit] Poll status for', editKey, ':', statusResult.status);

      if (!statusResult.success) {
        throw new Error(statusResult.error || 'Status check failed');
      }

      const status = statusResult.status;

      if (status === 'COMPLETED') {
        // Stop polling
        clearInterval(editPollingIntervals[editKey]);
        delete editPollingIntervals[editKey];

        // Get result
        const resultResponse = await fetch(getApiUrl(EDIT_ENDPOINTS.result(modelId, requestId)), {
          headers: authHeaders
        });
        const resultData = await resultResponse.json();

        if (!resultData.success) {
          throw new Error(resultData.error || 'Failed to get result');
        }

        // Extract image URL
        const imageUrl = extractImageUrl(resultData);

        if (imageUrl) {
          // Update state
          editGenerations[editKey] = {
            ...editGenerations[editKey],
            status: 'COMPLETED',
            imageUrl
          };

          // Route to correct target based on captured context
          await handleEditSuccess(ideaId, imageUrl, context);
          console.log('[ImageEdit] Edit completed for', editKey, ':', imageUrl);

          // Clean up state after successful completion
          delete editGenerations[editKey];
        } else {
          throw new Error('No image in response');
        }

      } else if (status === 'FAILED') {
        clearInterval(editPollingIntervals[editKey]);
        delete editPollingIntervals[editKey];
        delete editGenerations[editKey];
        showEditError(ideaId, statusResult.error || 'Edit failed', originalImageUrl);
      }
      // else: IN_QUEUE or IN_PROGRESS - continue polling

    } catch (error) {
      console.error('[ImageEdit] Poll error for', editKey, ':', error);
      clearInterval(editPollingIntervals[editKey]);
      delete editPollingIntervals[editKey];
      delete editGenerations[editKey];
      showEditError(ideaId, error.message, originalImageUrl);
    }

  }, POLL_INTERVAL);
}

/**
 * Extract image URL from various response formats
 * @param {Object} response - API response
 * @returns {string|null} Image URL or null
 */
function extractImageUrl(response) {
  // Handle various response structures
  if (response.data?.images?.[0]?.url) {
    return response.data.images[0].url;
  }
  if (response.data?.data?.images?.[0]?.url) {
    return response.data.data.images[0].url;
  }
  if (response.images?.[0]?.url) {
    return response.images[0].url;
  }
  return null;
}

// ============================================================================
// UI UPDATES
// ============================================================================

/**
 * Show editing placeholder on card
 * @param {string} ideaId - The idea ID
 */
function showEditingPlaceholder(ideaId) {
  const card = document.querySelector(`.idea-card[data-id="${ideaId}"]`);
  if (!card) return;

  const imageContainer = card.querySelector('.idea-image-container');
  if (!imageContainer) return;

  imageContainer.innerHTML = `
    <div class="image-placeholder editing">
      <div class="pixel-loader"></div>
      <span class="placeholder-text">EDITING...</span>
    </div>
  `;
}

/**
 * Show edit error on card
 * @param {string} ideaId - The idea ID
 * @param {string} message - Error message
 * @param {string} originalImageUrl - Original image URL for restore
 */
function showEditError(ideaId, message, originalImageUrl) {
  // Restore original image
  if (originalImageUrl) {
    updateIdeaImage(ideaId, originalImageUrl);
  }

  // Show error notification
  console.error('[ImageEdit] Error:', message);
  showError(message, { context: 'editing image' });
}

/**
 * Update the idea's image in the card and state
 * @param {string} ideaId - The idea ID (frontend ID like A1-171...)
 * @param {string} imageUrl - New image URL
 */
function updateIdeaImage(ideaId, imageUrl) {
  // Update state (use frontend ID for local cache)
  if (!GeneratorState.preloadedImages) {
    GeneratorState.preloadedImages = {};
  }
  GeneratorState.preloadedImages[ideaId] = imageUrl;

  // Task 102: Track latest media timestamp for display priority (edited image counts as new image)
  if (!GeneratorState.latestMediaTimestamp) GeneratorState.latestMediaTimestamp = {};
  GeneratorState.latestMediaTimestamp[String(ideaId)] = {
    type: 'image',
    timestamp: Date.now()
  };

  // Persist immediately so timestamp survives page refresh
  persistSessionState();

  // Resolve Database UUID from idMapping
  // Frontend IDs (e.g., A1-1735...) need to be mapped to DB UUIDs for saving
  let dbId = ideaId;
  if (GeneratorState.idMapping && GeneratorState.idMapping[ideaId]) {
    dbId = GeneratorState.idMapping[ideaId];
    console.log(`[ImageEdit] Mapped frontend ID ${ideaId} to DB ID ${dbId}`);
  }

  // Save to database (async, don't block UI but handle errors)
  // Only attempt if we have a valid-looking UUID (36 chars for UUID format)
  if (dbId && dbId.length >= 32 && !dbId.includes('-print-') && !dbId.includes('-video-') && !dbId.includes('-social-')) {
    saveImageToDatabase(dbId, imageUrl, {
      provider: 'nano-banana',
      aspectRatio: getSelectedAspectRatio()
    }).then(res => {
      if (res.success) {
        console.log('[ImageEdit] Edit saved to database successfully');
      } else {
        console.warn('[ImageEdit] Failed to save edit to database:', res.error);
        if (window.Toast) window.Toast.error('Edit applied but failed to save. Image may be lost on refresh.');
      }
    });
  } else {
    console.warn('[ImageEdit] Cannot save to DB: No valid UUID found for idea', ideaId, '- image is local only');
    if (window.Toast) window.Toast.warning('Edit applied but cannot save to database.');
  }

  // Update card UI
  const card = document.querySelector(`.idea-card[data-id="${ideaId}"]`);
  if (!card) return;

  const imageContainer = card.querySelector('.idea-image-container');
  if (!imageContainer) return;

  // Render image with all action buttons including history, edit, and make video
  imageContainer.innerHTML = `
    <div class="generated-image-wrapper">
      <img src="${imageUrl}" alt="Generated image" class="generated-image" loading="lazy" />
      <div class="image-actions">
        ${renderHistoryButton(ideaId)}
        <button class="btn-img-action btn-preview" data-url="${imageUrl}" title="View Full">
          <svg viewBox="0 0 16 16">
            <rect x="1" y="1" width="4" height="2"/>
            <rect x="1" y="1" width="2" height="4"/>
            <rect x="11" y="1" width="4" height="2"/>
            <rect x="13" y="1" width="2" height="4"/>
            <rect x="1" y="13" width="4" height="2"/>
            <rect x="1" y="11" width="2" height="4"/>
            <rect x="11" y="13" width="4" height="2"/>
            <rect x="13" y="11" width="2" height="4"/>
          </svg>
        </button>
        <button class="btn-img-action btn-download" data-url="${imageUrl}" title="Download">
          <svg viewBox="0 0 12 12">
            <path d="M6 0v8M2 5l4 5 4-5" stroke="black" stroke-width="2" fill="none"/>
          </svg>
        </button>
        <button class="btn-img-action btn-edit" title="Edit Image">
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M12.146 1.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1 0 .708l-9 9a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l9-9zM11.207 2.5L13.5 4.793 12.793 5.5 10.5 3.207 11.207 2.5zm-1.414 1.414L12.5 6.621 6.621 12.5l-2.707-2.707L9.793 3.914zM3.5 11.5l1.5 1.5-2.257.752L3.5 11.5z"/>
          </svg>
        </button>
        <button class="btn-img-action btn-make-video" data-id="${ideaId}" data-image-url="${imageUrl}" title="Make Video">
          <svg viewBox="0 0 16 16" fill="currentColor">
            <rect x="1" y="2" width="14" height="2"/>
            <rect x="1" y="12" width="14" height="2"/>
            <rect x="1" y="2" width="2" height="12"/>
            <rect x="13" y="2" width="2" height="12"/>
            <rect x="5" y="5" width="2" height="6"/>
            <rect x="7" y="6" width="2" height="4"/>
            <rect x="9" y="7" width="2" height="2"/>
          </svg>
        </button>
      </div>
    </div>
  `;

  // Also update the idea canvas if it's open and showing this idea
  if (window.IdeaCanvas?.isOpen?.()) {
    const currentIdea = window.IdeaCanvas.getCurrentIdea?.();
    // Check against both the ideaId we have and the resolved dbId
    if (currentIdea && (String(currentIdea.id) === ideaId || String(currentIdea.id) === dbId)) {
      currentIdea.imageUrl = imageUrl;
      currentIdea.image_url = imageUrl;
      window.IdeaCanvas.refresh?.();
      console.log('[ImageEdit] Refreshed canvas with edited image');
    }
  }
}

// ============================================================================
// HELPERS
// ============================================================================

// getIdeaImageUrl is imported from image-generation.js (Single Source of Truth)

/**
 * Find an idea by ID in generated ideas
 * @param {string} ideaId - The idea ID
 * @returns {Object|null} Idea object or null
 */
function findIdea(ideaId) {
  const generatedIdeas = GeneratorState.generatedIdeas || [];
  for (const group of generatedIdeas) {
    const found = group.ideas?.find(i => i.id === ideaId);
    if (found) return found;
  }
  return null;
}

/**
 * Get selected resolution from UI
 * @returns {string} Resolution value
 */
function getSelectedResolution() {
  const active = document.querySelector('.resolution-btn.active');
  return active?.dataset?.resolution || '1K';
}

/**
 * Get selected aspect ratio from UI
 * @returns {string} Aspect ratio value
 */
function getSelectedAspectRatio() {
  const active = document.querySelector('.edit-aspect-btn.active');
  return active?.dataset?.aspect || 'auto';
}

/**
 * Get selected style from UI
 * @returns {string} Style value
 */
function getSelectedStyle() {
  return document.getElementById('edit-style-selector')?.value || 'auto';
}

/**
 * Build styled prompt by prepending style guide to user prompt
 * @param {string} userPrompt - The user's edit description
 * @param {string} style - The selected style
 * @returns {string} The prompt with style guide prepended
 */
function buildEditPromptWithStyle(userPrompt, style) {
  const effectiveStyle = getEffectiveStyle(style);
  const styleGuide = STYLE_GUIDES[effectiveStyle] || '';

  if (!styleGuide) {
    return userPrompt;
  }

  return `${styleGuide}\n\n${userPrompt}`;
}

/**
 * Reset edit options to defaults
 */
function resetEditOptions() {
  // Reset resolution
  const resolutionBtns = document.querySelectorAll('.resolution-btn');
  resolutionBtns.forEach(btn => {
    btn.classList.toggle('active', btn.dataset.resolution === '1K');
  });

  // Reset aspect ratio
  const aspectBtns = document.querySelectorAll('.edit-aspect-btn');
  aspectBtns.forEach(btn => {
    btn.classList.toggle('active', btn.dataset.aspect === 'auto');
  });

  // Reset style to 'auto'
  const styleSelector = document.getElementById('edit-style-selector');
  if (styleSelector) {
    styleSelector.value = 'auto';
  }
}

/**
 * Set submit button loading state
 * @param {boolean} loading - Whether loading
 */
function setSubmitLoading(loading) {
  const submitBtn = document.getElementById('edit-modal-submit');
  if (!submitBtn) return;

  const textEl = submitBtn.querySelector('.btn-text');
  const loadingEl = submitBtn.querySelector('.btn-loading');

  if (loading) {
    submitBtn.disabled = true;
    if (textEl) textEl.style.display = 'none';
    if (loadingEl) loadingEl.style.display = 'flex';
  } else {
    submitBtn.disabled = false;
    if (textEl) textEl.style.display = 'inline';
    if (loadingEl) loadingEl.style.display = 'none';
  }
}

/**
 * Reset submit button state
 */
function resetSubmitButton() {
  setSubmitLoading(false);
}

// ============================================================================
// GLOBAL EXPORTS
// ============================================================================

// Export openEditModal globally for access from storyboard/ui.js
window.openEditModal = openEditModal;
